import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import {
  NOTIFICATIONS_PRIORITY,
  PaginatedNotificationsList,
} from "Shared/Types/notifications";
import {
  checkNotificationsCount,
  checkUrgentNotifications,
  getNotifications,
  getNotificationsMore,
  markAllAsRead,
  deleteAllNotifications,
} from "./reducer";

export type NotificationView = "all" | "unread";
type ViewSnapshot = { data: PaginatedNotificationsList; loadedAt: number };
interface INotificationTypeState {
  hasUrgentNotifications: boolean;
  priorityNotificationsCount: number;
  notificationsCount: number;
  loading: boolean;
  hasLoaded: boolean;
  actionLoading: boolean;
  activeView: NotificationView;
  cacheScope: string | null;
  views: Partial<Record<NotificationView, ViewSnapshot>>;
  notifications: PaginatedNotificationsList;
  countLoading: boolean;
  priorityLoading: boolean;
  countFetchedAt: number;
  priorityFetchedAt: number;
  error: string | null;
  failedPage: boolean;
  requestId?: string;
  countRequestId?: string;
  urgentRequestId?: string;
}
const emptyPage = (): PaginatedNotificationsList => ({
  count: 0,
  next: null,
  results: [],
});
const copyPage = (
  page: PaginatedNotificationsList,
): PaginatedNotificationsList => ({
  ...page,
  results: page.results.map((item) => ({ ...item })),
});
const initialState: INotificationTypeState = {
  hasUrgentNotifications: false,
  priorityNotificationsCount: 0,
  notificationsCount: 0,
  loading: false,
  hasLoaded: false,
  actionLoading: false,
  activeView: "all",
  cacheScope: null,
  views: {},
  notifications: emptyPage(),
  countLoading: false,
  priorityLoading: false,
  countFetchedAt: 0,
  priorityFetchedAt: 0,
  error: null,
  failedPage: false,
};
const isPriority = (priority: NOTIFICATIONS_PRIORITY) =>
  [NOTIFICATIONS_PRIORITY.URGENT, NOTIFICATIONS_PRIORITY.HIGH].includes(
    priority,
  );

const notificationSlice = createSlice({
  name: "notificationSlice",
  initialState,
  reducers: {
    setNotificationScope: (state, action: PayloadAction<string>) => {
      if (state.cacheScope === action.payload) return;
      return {
        ...initialState,
        cacheScope: action.payload,
        notifications: emptyPage(),
        views: {},
      };
    },
    showNotificationView: (state, action: PayloadAction<NotificationView>) => {
      if (state.activeView === action.payload) return;
      state.activeView = action.payload;
      state.requestId = undefined;
      state.loading = false;
      state.error = null;
      const cached = state.views[action.payload];
      if (cached) {
        state.notifications = copyPage(cached.data);
        state.hasLoaded = true;
      } else if (action.payload === "unread" && state.views.all) {
        const all = state.views.all;
        const results = all.data.results.filter((item) => !item.is_read);
        state.notifications = {
          results: results.map((item) => ({ ...item })),
          next: null,
          count: state.notificationsCount,
        };
        state.hasLoaded = results.length > 0 || !all.data.next;
        if (!all.data.next) {
          state.notifications.count = results.length;
          state.views.unread = {
            data: copyPage(state.notifications),
            loadedAt: all.loadedAt,
          };
        }
      } else {
        state.notifications = emptyPage();
        state.hasLoaded = false;
      }
    },
    hideAllNotificationsState: (state) => {
      state.notificationsCount = 0;
      state.priorityNotificationsCount = 0;
      state.hasUrgentNotifications = false;
      state.notifications.results = state.notifications.results.map((item) => ({
        ...item,
        is_read: true,
      }));
      if (state.views.all)
        state.views.all.data.results = state.views.all.data.results.map(
          (item) => ({ ...item, is_read: true }),
        );
      state.views.unread = { data: emptyPage(), loadedAt: Date.now() };
      if (state.activeView === "unread") state.notifications = emptyPage();
      state.hasLoaded = true;
      state.requestId = undefined;
      state.countRequestId = undefined;
      state.urgentRequestId = undefined;
      state.loading = false;
      state.countLoading = false;
      state.priorityLoading = false;
      state.countFetchedAt = Date.now();
      state.priorityFetchedAt = Date.now();
    },
    deleteAllNotificationsState: (state) => {
      state.notificationsCount = 0;
      state.priorityNotificationsCount = 0;
      state.hasUrgentNotifications = false;
      state.notifications = emptyPage();
      state.views = {
        all: { data: emptyPage(), loadedAt: Date.now() },
        unread: { data: emptyPage(), loadedAt: Date.now() },
      };
      state.hasLoaded = true;
      state.requestId = undefined;
      state.countRequestId = undefined;
      state.urgentRequestId = undefined;
      state.loading = false;
      state.countLoading = false;
      state.priorityLoading = false;
      state.countFetchedAt = Date.now();
      state.priorityFetchedAt = Date.now();
      state.error = null;
    },
    markNotificationRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.results.find(
        (item) => item.id === action.payload,
      );
      if (notification && !notification.is_read) {
        state.notificationsCount = Math.max(0, state.notificationsCount - 1);
        if (isPriority(notification.priority))
          state.priorityNotificationsCount = Math.max(
            0,
            state.priorityNotificationsCount - 1,
          );
        state.hasUrgentNotifications = state.priorityNotificationsCount > 0;
        state.notifications.results = state.notifications.results.map((item) =>
          item.id === action.payload ? { ...item, is_read: true } : item,
        );
        if (state.activeView === "unread") {
          state.notifications.results = state.notifications.results.filter(
            (item) => !item.is_read,
          );
          state.notifications.count = Math.max(
            0,
            state.notifications.count - 1,
          );
        }
      }
      Object.entries(state.views).forEach(([view, cached]) => {
        if (!cached) return;
        const wasUnread = cached.data.results.some(
          (item) => item.id === action.payload && !item.is_read,
        );
        cached.data.results = cached.data.results.map((item) =>
          item.id === action.payload ? { ...item, is_read: true } : item,
        );
        if (view === "unread") {
          cached.data.results = cached.data.results.filter(
            (item) => !item.is_read,
          );
          if (wasUnread) cached.data.count = Math.max(0, cached.data.count - 1);
        }
        cached.loadedAt = 0;
      });
      state.requestId = undefined;
      state.countRequestId = undefined;
      state.urgentRequestId = undefined;
      state.loading = false;
      state.countLoading = false;
      state.priorityLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotifications.fulfilled, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.error = null;
      state.notifications = copyPage(action.payload);
      state.loading = false;
      state.hasLoaded = true;
      state.views[state.activeView] = {
        data: copyPage(action.payload),
        loadedAt: Date.now(),
      };
    });
    builder.addCase(getNotifications.pending, (state, action) => {
      state.activeView = action.meta.arg?.is_read === false ? "unread" : "all";
      state.requestId = action.meta.requestId;
      state.error = null;
      state.loading = true;
    });
    builder.addCase(getNotifications.rejected, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.loading = false;
      state.failedPage = false;
      state.error = action.error.message || "Unable to load notifications";
    });
    builder.addCase(getNotificationsMore.fulfilled, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.error = null;
      const existingIds = new Set(
        state.notifications.results.map((item) => item.id),
      );
      state.notifications = {
        results: [
          ...state.notifications.results,
          ...action.payload.results.filter((item) => !existingIds.has(item.id)),
        ],
        next: action.payload.next,
        count: action.payload.count,
      };
      state.loading = false;
      state.hasLoaded = true;
      state.views[state.activeView] = {
        data: copyPage(state.notifications),
        loadedAt: Date.now(),
      };
    });
    builder.addCase(getNotificationsMore.pending, (state, action) => {
      state.requestId = action.meta.requestId;
      state.error = null;
      state.loading = true;
    });
    builder.addCase(getNotificationsMore.rejected, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.loading = false;
      state.failedPage = true;
      state.error = action.error.message || "Unable to load more notifications";
    });
    builder.addCase(checkUrgentNotifications.pending, (state, action) => {
      state.urgentRequestId = action.meta.requestId;
      state.priorityLoading = true;
    });
    builder.addCase(checkUrgentNotifications.fulfilled, (state, action) => {
      if (state.urgentRequestId !== action.meta.requestId) return;
      state.priorityNotificationsCount = action.payload.count;
      state.hasUrgentNotifications = action.payload.count > 0;
      state.priorityLoading = false;
      state.priorityFetchedAt = Date.now();
    });
    builder.addCase(checkUrgentNotifications.rejected, (state, action) => {
      if (state.urgentRequestId === action.meta.requestId)
        state.priorityLoading = false;
    });
    builder.addCase(checkNotificationsCount.pending, (state, action) => {
      state.countRequestId = action.meta.requestId;
      state.countLoading = true;
    });
    builder.addCase(checkNotificationsCount.fulfilled, (state, action) => {
      if (state.countRequestId !== action.meta.requestId) return;
      if (state.notificationsCount !== action.payload.count)
        Object.values(state.views).forEach((view) => {
          if (view) view.loadedAt = 0;
        });
      state.notificationsCount = action.payload.count;
      state.countLoading = false;
      state.countFetchedAt = Date.now();
    });
    builder.addCase(checkNotificationsCount.rejected, (state, action) => {
      if (state.countRequestId === action.meta.requestId)
        state.countLoading = false;
    });
    [markAllAsRead, deleteAllNotifications].forEach((action) => {
      builder.addCase(action.pending, (state) => {
        state.actionLoading = true;
      });
      builder.addCase(action.fulfilled, (state) => {
        state.actionLoading = false;
      });
      builder.addCase(action.rejected, (state) => {
        state.actionLoading = false;
      });
    });
  },
});
export const {
  showNotificationView,
  setNotificationScope,
  hideAllNotificationsState,
  markNotificationRead,
  deleteAllNotificationsState,
} = notificationSlice.actions;
export const selectNotificationsCount = (state: RootState) =>
  state.notificationSlice.notificationsCount;
export const selectUrgentNotificationsCount = (state: RootState) =>
  state.notificationSlice.hasUrgentNotifications;
export const selectPriorityNotificationsCount = (state: RootState) =>
  state.notificationSlice.priorityNotificationsCount;
export const selectNotifications = (state: RootState) =>
  state.notificationSlice.notifications;
export const selectNotificationsState = (state: RootState) =>
  state.notificationSlice;
export const selectNotificationLoading = (state: RootState) =>
  state.notificationSlice.loading;
export default notificationSlice;
